import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Plus, Pencil, Trash2 } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import {
  getGPS, getOpponents, getPlayers, getMatchAvailableDrills, getMatchDrillMetrics,
  getComparisonPresets, createComparisonPreset, deleteComparisonPreset,
} from '../../api';
import { useSeason, dateToSeason } from '../../SeasonContext';
import { METRICS, PLAYER_COLORS } from './InMatchAnalysis';

const POSITIONS = ['All', 'GK', 'CB', 'FB', 'CDM', 'CAM', 'ST', 'W'];
const MAX_PLAYERS = 5;

const fmtDate = d => { if (!d) return ''; const [, m, day] = d.split('-'); return `${day}/${m}`; };
// 1 decimal for values under 100 (e.g. 10.3), whole numbers from 100 up (e.g. 100).
const fmt0 = n => {
  if (n == null || isNaN(n)) return '—';
  return Math.abs(n) >= 100 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
};
const STORAGE_KEY = 'comparison_builder_last_state';

let nextSlotId = 1;
const newSlot = () => ({ id: nextSlotId++, drills: [], customLabel: null });

// ── Drill multi-select for a single slot ────────────────────────────────────
function DrillPicker({ availableDrills, usedInOtherSlots, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = drill => onChange(
    selected.includes(drill) ? selected.filter(d => d !== drill) : [...selected, drill]
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 7,
        border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)',
        fontSize: '0.78rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
      }}>
        <Plus size={13} /> Add drills
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10,
          minWidth: 190, maxHeight: 280, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', padding: '5px 0',
        }}>
          {availableDrills.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-dimmed)' }}>No segments for this match</div>
          ) : availableDrills.map(d => (
            <label key={d.drill} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-primary)', background: selected.includes(d.drill) ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
              <input type="checkbox" checked={selected.includes(d.drill)} onChange={() => toggle(d.drill)} style={{ accentColor: '#3b82f6', flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{d.start_min}–{d.end_min}'</span>
              {usedInOtherSlots.has(d.drill) && <span style={{ fontSize: '0.62rem', color: 'var(--text-dimmed)' }}>used</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Multi-player select, capped at MAX_PLAYERS ──────────────────────────────
function PlayerMultiSelect({ players, selected, onChange, max = MAX_PLAYERS }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = id => {
    const idStr = String(id);
    if (selected.includes(idStr)) onChange(selected.filter(s => s !== idStr));
    else if (selected.length < max) onChange([...selected, idStr]);
  };

  const label = selected.length === 0 ? 'Select players…'
    : selected.length === 1 ? (players.find(p => String(p.id) === selected[0])?.name || '1 player')
    : `${selected.length} players`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 12px', minWidth: 150,
        borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)',
        color: 'var(--text-primary)', fontSize: '0.83rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
      }}>
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10,
          minWidth: 210, maxHeight: 300, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', padding: '5px 0',
        }}>
          <div style={{ padding: '4px 12px 8px', fontSize: '0.66rem', color: 'var(--text-dimmed)' }}>Up to {max} players</div>
          {players.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-dimmed)' }}>No players</div>
          ) : players.map(p => {
            const idStr = String(p.id);
            const checked = selected.includes(idStr);
            const disabled = !checked && selected.length >= max;
            return (
              <label key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                cursor: disabled ? 'default' : 'pointer', fontSize: '0.8rem', color: 'var(--text-primary)',
                opacity: disabled ? 0.4 : 1, background: checked ? 'rgba(59,130,246,0.07)' : 'transparent',
              }}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(p.id)} style={{ accentColor: '#3b82f6', flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{p.name}</span>
                {p.position && <span style={{ fontSize: '0.62rem', color: 'var(--text-dimmed)' }}>{p.position}</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SlotCard({ slot, index, availableDrills, usedInOtherSlots, autoLabel, onSetDrills, onSetLabel, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(slot.customLabel ?? autoLabel);

  const commitLabel = () => {
    setEditing(false);
    onSetLabel(draft.trim() || null);
  };

  return (
    <div style={{ background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: 12, minWidth: 220, flex: '1 1 220px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 6 }}>
        {editing ? (
          <input
            autoFocus value={draft} onChange={e => setDraft(e.target.value)}
            onBlur={commitLabel} onKeyDown={e => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setDraft(slot.customLabel ?? autoLabel); setEditing(false); } }}
            style={{ flex: 1, fontSize: '0.83rem', fontWeight: 700, padding: '3px 6px', borderRadius: 6, border: '1px solid #3b82f6', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' }}
          />
        ) : (
          <div onClick={() => { setDraft(slot.customLabel ?? autoLabel); setEditing(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1, overflow: 'hidden' }}>
            <span style={{ fontSize: '0.83rem', fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Slot {index + 1}: {slot.customLabel ?? autoLabel}
            </span>
            <Pencil size={11} color="var(--text-dimmed)" style={{ flexShrink: 0 }} />
          </div>
        )}
        <button onClick={onRemove} style={{ background: 'transparent', border: 'none', color: 'var(--text-dimmed)', cursor: 'pointer', padding: 2, flexShrink: 0, display: 'flex' }}>
          <X size={15} />
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8, minHeight: slot.drills.length ? 'auto' : 0 }}>
        {slot.drills.map(title => {
          const meta = availableDrills.find(d => d.drill === title);
          return (
            <span key={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 600, color: '#93c5fd', background: 'rgba(59,130,246,0.14)', borderRadius: 99, padding: '3px 6px 3px 9px' }}>
              {meta ? `${meta.start_min}–${meta.end_min}'` : title}
              <X size={11} style={{ cursor: 'pointer' }} onClick={() => onSetDrills(slot.drills.filter(d => d !== title))} />
            </span>
          );
        })}
      </div>

      <DrillPicker
        availableDrills={availableDrills}
        usedInOtherSlots={usedInOtherSlots}
        selected={slot.drills}
        onChange={onSetDrills}
      />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ComparisonBuilder({ onNavigate, user }) {
  const { selectedSeasons } = useSeason();
  const [sessions,   setSessions]   = useState([]);
  const [opponents,  setOpponents]  = useState([]);
  const [players,    setPlayers]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [matchId,    setMatchId]    = useState('');

  const [availableDrills, setAvailableDrills] = useState([]);
  const [drillsLoading,   setDrillsLoading]   = useState(false);

  const [scope,          setScope]          = useState('team'); // 'team' | 'player'
  const [playerIds,      setPlayerIds]      = useState([]); // up to MAX_PLAYERS ids, as strings
  const [positionFilter, setPositionFilter] = useState('All');
  const [metric1,  setMetric1]  = useState('hmld');
  const [metric2,  setMetric2]  = useState(''); // '' = none
  const [aggMode,  setAggMode]  = useState('sum'); // 'sum' | 'avg_per_min'

  // { [seriesKey]: rows[] } — seriesKey is '__team' for team scope, or a player id
  const [drillMetricsBySeries, setDrillMetricsBySeries] = useState({});
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [slots, setSlots] = useState(() => [newSlot()]);

  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetNameDraft, setPresetNameDraft] = useState('');

  // ── Fetch match sessions + opponents + players + presets, then restore the
  //    last-used build (or the match handed off from In-Match Analysis) ─────────
  useEffect(() => {
    Promise.all([getGPS(), getOpponents(), getPlayers(), getComparisonPresets()])
      .then(([gps, opps, ps, pr]) => {
        const matches = (gps || []).filter(s => s.session_type === 'match')
          .sort((a, b) => (b.session_date || '').localeCompare(a.session_date || ''));
        setSessions(matches);
        setOpponents(opps || []);
        setPlayers((ps || []).filter(p => p.active).sort((a, b) => a.name.localeCompare(b.name)));
        setPresets((pr || []).sort((a, b) => a.name.localeCompare(b.name)));

        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { /* ignore malformed */ }

        const handoff = localStorage.getItem('comparison_builder_match_id');
        localStorage.removeItem('comparison_builder_match_id');

        const resolvedMatchId =
          (handoff && matches.some(m => String(m.id) === String(handoff))) ? String(handoff)
          : (saved?.matchId && matches.some(m => String(m.id) === String(saved.matchId))) ? String(saved.matchId)
          : (matches.length ? String(matches[0].id) : '');
        setMatchId(resolvedMatchId);

        if (saved) {
          setScope(saved.scope || 'team');
          // Backward-compatible with the earlier single-playerId save shape.
          setPlayerIds(Array.isArray(saved.playerIds) ? saved.playerIds : (saved.playerId ? [saved.playerId] : []));
          setPositionFilter(saved.positionFilter || 'All');
          setMetric1(saved.metric1 || 'hmld');
          setMetric2(saved.metric2 || '');
          setAggMode(saved.aggMode || 'sum');
          if (Array.isArray(saved.slots) && saved.slots.length) {
            setSlots(saved.slots.map(s => ({ id: nextSlotId++, drills: s.drills || [], customLabel: s.customLabel ?? null })));
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Persist the current build so returning to the page (e.g. via In-Match
  // Analysis and back) shows the same slots/scope/metrics again.
  const playerIdsKey = playerIds.join(',');
  useEffect(() => {
    if (loading) return; // don't clobber saved state with defaults before restore runs
    const toSave = {
      matchId, scope, playerIds, positionFilter, metric1, metric2, aggMode,
      slots: slots.map(s => ({ drills: s.drills, customLabel: s.customLabel })),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave)); } catch { /* storage full/unavailable */ }
  }, [matchId, scope, playerIdsKey, positionFilter, metric1, metric2, aggMode, slots, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredSessions = useMemo(
    () => sessions.filter(s => {
      const d = s.session_date?.slice(0, 10);
      if (!d) return true;
      const season = dateToSeason(d);
      return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
    }),
    [sessions, selectedSeasons],
  );

  // ── Available drill segments for the selected match ───────────────────────────
  useEffect(() => {
    if (!matchId) { setAvailableDrills([]); return; }
    setDrillsLoading(true);
    getMatchAvailableDrills(matchId)
      .then(d => setAvailableDrills(d || []))
      .catch(() => setAvailableDrills([]))
      .finally(() => setDrillsLoading(false));
  }, [matchId]);

  // 2+ players compares them as ranked bars (one per player); a 2nd metric/dual
  // axis only makes sense for a single series (team or exactly one player).
  const isMultiPlayer = scope === 'player' && playerIds.length > 1;

  // Clear the 2nd metric when entering multi-player mode so a stale dual-axis
  // selection doesn't linger once it's no longer applicable.
  useEffect(() => {
    if (isMultiPlayer && metric2) setMetric2('');
  }, [isMultiPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Raw per-drill metric values — one fetch per series (team, or per player),
  //    re-fetched on scope/player/metric change. Slot bucketing then happens
  //    entirely client-side from this raw data. ─────────────────────────────────
  useEffect(() => {
    if (!matchId) { setDrillMetricsBySeries({}); return; }
    if (scope === 'player' && playerIds.length === 0) { setDrillMetricsBySeries({}); return; }
    const metrics = isMultiPlayer ? [metric1] : [metric1, metric2].filter(Boolean);
    if (!metrics.length) { setDrillMetricsBySeries({}); return; }
    setMetricsLoading(true);
    const fetches = scope === 'team'
      ? [getMatchDrillMetrics(matchId, { scope: 'team', metrics }).then(rows => ['__team', rows || []])]
      : playerIds.map(id => getMatchDrillMetrics(matchId, { scope: 'player', playerId: id, metrics }).then(rows => [String(id), rows || []]));
    Promise.all(fetches)
      .then(entries => {
        const map = {};
        entries.forEach(([key, rows]) => { map[key] = rows; });
        setDrillMetricsBySeries(map);
      })
      .catch(() => setDrillMetricsBySeries({}))
      .finally(() => setMetricsLoading(false));
  }, [matchId, scope, playerIdsKey, metric1, metric2, isMultiPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  const drillMetricsByTitleBySeries = useMemo(() => {
    const out = {};
    Object.entries(drillMetricsBySeries).forEach(([key, rows]) => {
      const map = {};
      rows.forEach(r => { map[r.drill] = r; });
      out[key] = map;
    });
    return out;
  }, [drillMetricsBySeries]);

  // One "series" per bar/line the chart draws: a single team-average series,
  // or one per selected player (colored, for the ranked multi-player view).
  const seriesList = useMemo(() => {
    if (scope === 'team') return [{ key: '__team', label: 'Squad average', color: '#3b82f6' }];
    return playerIds.map((id, i) => {
      const p = players.find(pl => String(pl.id) === String(id));
      return { key: String(id), label: p?.name || `Player ${id}`, color: PLAYER_COLORS[i % PLAYER_COLORS.length] };
    });
  }, [scope, playerIds, players]);

  // ── Slot helpers ───────────────────────────────────────────────────────────────
  const addSlot = () => setSlots(prev => [...prev, newSlot()]);
  const removeSlot = id => setSlots(prev => prev.length > 1 ? prev.filter(s => s.id !== id) : prev);
  const setSlotDrills = (id, drills) => setSlots(prev => prev.map(s => s.id === id ? { ...s, drills } : s));
  const setSlotLabel = (id, label) => setSlots(prev => prev.map(s => s.id === id ? { ...s, customLabel: label } : s));

  const autoLabel = slot => {
    if (!slot.drills.length) return 'Empty slot';
    const meta = slot.drills.map(t => availableDrills.find(d => d.drill === t)).filter(Boolean);
    if (!meta.length) return slot.drills.join(', ');
    const start = Math.min(...meta.map(m => m.start_min));
    const end = Math.max(...meta.map(m => m.end_min));
    return `${start}-${end}'`;
  };

  const usedByOtherSlot = slotId => {
    const set = new Set();
    slots.forEach(s => { if (s.id !== slotId) s.drills.forEach(d => set.add(d)); });
    return set;
  };

  // ── Presets ────────────────────────────────────────────────────────────────────
  // A preset stores a bucket size (minutes), not a fixed drill list, so it
  // regenerates the right number of slots for whatever match/format is open.
  const applyPreset = preset => {
    setMetric1(preset.metric1);
    setMetric2(preset.metric2 || '');
    setAggMode(preset.agg_mode || 'sum');
    const sorted = [...availableDrills].sort((a, b) => a.start_min - b.start_min);
    const buckets = [];
    let current = [];
    let bucketStart = null;
    sorted.forEach(d => {
      if (bucketStart === null) bucketStart = d.start_min;
      current.push(d.drill);
      if (d.end_min - bucketStart >= preset.bucket_minutes) {
        buckets.push(current);
        current = [];
        bucketStart = null;
      }
    });
    if (current.length) buckets.push(current);
    setSlots(buckets.length ? buckets.map(drills => ({ id: nextSlotId++, drills, customLabel: null })) : [newSlot()]);
    setSelectedPresetId(String(preset.id));
  };

  const handleSavePreset = async () => {
    const name = presetNameDraft.trim();
    if (!name) return;
    const firstFilled = slots.find(s => s.drills.length > 0);
    if (!firstFilled) { alert('Add at least one slot with drills before saving a preset.'); return; }
    const meta = firstFilled.drills.map(t => availableDrills.find(d => d.drill === t)).filter(Boolean);
    const bucketMinutes = meta.length ? Math.max(...meta.map(m => m.end_min)) - Math.min(...meta.map(m => m.start_min)) : 15;
    try {
      const created = await createComparisonPreset({
        name, bucket_minutes: bucketMinutes, metric1, metric2: metric2 || null, agg_mode: aggMode, created_by: user?.id || null,
      });
      setPresets(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedPresetId(String(created.id));
      setPresetNameDraft('');
      setSavingPreset(false);
    } catch { alert('Failed to save preset.'); }
  };

  const handleDeletePreset = async id => {
    if (!window.confirm('Delete this preset? Other accounts will no longer see it.')) return;
    try {
      await deleteComparisonPreset(id);
      setPresets(prev => prev.filter(p => p.id !== id));
      if (String(selectedPresetId) === String(id)) setSelectedPresetId('');
    } catch { alert('Failed to delete preset.'); }
  };

  // Sum of the slot's selected drills' values for a metric/series, or (if
  // avg-per-minute is on) that sum divided by the slot's total nominal minutes
  // (e.g. 3x 5-min drills = 15 min denominator) — applies globally, not per slot.
  const computeSlotValue = (slot, metricKey, seriesKey) => {
    const map = drillMetricsByTitleBySeries[seriesKey];
    if (!metricKey || !slot.drills.length || !map) return null;
    let sum = 0, totalMin = 0, any = false;
    slot.drills.forEach(title => {
      const row = map[title];
      if (!row) return;
      const v = row[metricKey];
      if (v != null) { sum += Number(v); any = true; }
      totalMin += Number(row.duration_min) || 0;
    });
    if (!any) return null;
    return aggMode === 'avg_per_min' && totalMin > 0 ? sum / totalMin : sum;
  };

  const filledSlots = useMemo(() => slots.filter(s => s.drills.length > 0), [slots]);

  // Consistent left-to-right order in every slot: ranked once by each player's
  // overall metric1 total across the whole comparison, not re-ranked per slot.
  const rankedSeriesList = useMemo(() => {
    if (!isMultiPlayer) return seriesList;
    const withTotals = seriesList.map(s => ({
      ...s,
      total: filledSlots.reduce((sum, slot) => sum + (computeSlotValue(slot, metric1, s.key) || 0), 0),
    }));
    return withTotals.sort((a, b) => b.total - a.total);
  }, [isMultiPlayer, seriesList, filledSlots, drillMetricsByTitleBySeries, metric1, aggMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = useMemo(() => filledSlots.map(s => {
    const entry = { label: s.customLabel ?? autoLabel(s) };
    if (isMultiPlayer) {
      rankedSeriesList.forEach(series => { entry[series.label] = computeSlotValue(s, metric1, series.key); });
    } else {
      const seriesKey = seriesList[0]?.key;
      entry[metric1] = computeSlotValue(s, metric1, seriesKey);
      if (metric2) entry[metric2] = computeSlotValue(s, metric2, seriesKey);
    }
    return entry;
  }), [filledSlots, drillMetricsByTitleBySeries, metric1, metric2, aggMode, availableDrills, isMultiPlayer, rankedSeriesList, seriesList]); // eslint-disable-line react-hooks/exhaustive-deps

  const metric1Label = METRICS.find(m => m.key === metric1)?.label || metric1;
  const metric2Label = METRICS.find(m => m.key === metric2)?.label || metric2;
  const unitSuffix = aggMode === 'avg_per_min' ? '/min' : '';

  const positionFilteredPlayers = useMemo(
    () => positionFilter === 'All' ? players : players.filter(p => p.position === positionFilter),
    [players, positionFilter]
  );
  // Choosing a position auto-fills up to MAX_PLAYERS players of that position —
  // "automatically compare all Wingers" — while still narrowing the picker so
  // manual add/remove afterward stays within that position too.
  const handlePositionChange = pos => {
    setPositionFilter(pos);
    if (pos !== 'All') {
      setPlayerIds(players.filter(p => p.position === pos).slice(0, MAX_PLAYERS).map(p => String(p.id)));
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────────
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };
  const SEL  = { padding: '6px 26px 6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.83rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none' };
  const segBtn = active => ({
    padding: '6px 14px', borderRadius: 7, border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`,
    background: active ? '#3b82f6' : 'var(--bg-card)', color: active ? '#fff' : 'var(--text-muted)',
    cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'inherit',
  });

  const selectedSession = filteredSessions.find(s => String(s.id) === matchId);
  const selectedOpp = opponents.find(o => o.id === selectedSession?.opponent_id);
  const sessionLabel = s => {
    const opp = opponents.find(o => o.id === s.opponent_id);
    const date = fmtDate(s.session_date?.slice(0, 10));
    return opp ? `${opp.name} · ${date}` : `${s.session_title || 'Match'} · ${date}`;
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</div>
  );

  if (!sessions.length) return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Comparison Builder</h2>
      <div style={{ ...card, padding: 48, textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>No match sessions found. Upload match CSVs in the Upload zone first.</p>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Comparison Builder</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Build a custom bar comparison across any grouping of this match's 5-minute segments
          </p>
        </div>
        {onNavigate && (
          <button onClick={() => onNavigate('gps', 'in-match-analysis')}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            ← Back to In-Match Analysis
          </button>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Match selector */}
        <div style={{ position: 'relative' }}>
          <select value={matchId} onChange={e => { setMatchId(e.target.value); setSlots([newSlot()]); setSelectedPresetId(''); }} style={{ ...SEL, minWidth: 220 }}>
            {filteredSessions.map(s => <option key={s.id} value={String(s.id)}>{sessionLabel(s)}</option>)}
          </select>
        </div>

        {/* Scope */}
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={segBtn(scope === 'team')} onClick={() => setScope('team')}>Team</button>
          <button style={segBtn(scope === 'player')} onClick={() => setScope('player')}>Player</button>
        </div>
        {scope === 'player' && (
          <>
            <select value={positionFilter} onChange={e => handlePositionChange(e.target.value)} style={SEL}>
              {POSITIONS.map(pos => <option key={pos} value={pos}>{pos === 'All' ? 'All positions' : pos}</option>)}
            </select>
            <PlayerMultiSelect players={positionFilteredPlayers} selected={playerIds} onChange={setPlayerIds} />
          </>
        )}

        {/* Metrics */}
        <select value={metric1} onChange={e => setMetric1(e.target.value)} style={SEL}>
          {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        {!isMultiPlayer && (
          <select value={metric2} onChange={e => setMetric2(e.target.value)} style={SEL}>
            <option value="">No 2nd metric</option>
            {METRICS.filter(m => m.key !== metric1).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        )}

        {/* Sum vs avg/min */}
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <button style={segBtn(aggMode === 'sum')} onClick={() => setAggMode('sum')}>Sum</button>
          <button style={segBtn(aggMode === 'avg_per_min')} onClick={() => setAggMode('avg_per_min')}>Avg / min</button>
        </div>
      </div>

      {/* ── Presets ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>Preset:</span>
        <select
          value={selectedPresetId}
          onChange={e => {
            const p = presets.find(pr => String(pr.id) === e.target.value);
            if (p) applyPreset(p); else setSelectedPresetId('');
          }}
          style={SEL}>
          <option value="">Custom (no preset)</option>
          {presets.map(p => <option key={p.id} value={p.id}>{p.name} ({p.bucket_minutes}min)</option>)}
        </select>
        {user?.isAdmin && selectedPresetId && (
          <button onClick={() => handleDeletePreset(selectedPresetId)} title="Delete this preset"
            style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: '#f87171', cursor: 'pointer' }}>
            <Trash2 size={13} />
          </button>
        )}
        {user?.isAdmin && (
          savingPreset ? (
            <>
              <input
                autoFocus value={presetNameDraft} onChange={e => setPresetNameDraft(e.target.value)}
                placeholder="Preset name…"
                onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); if (e.key === 'Escape') { setSavingPreset(false); setPresetNameDraft(''); } }}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #3b82f6', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.83rem', fontFamily: 'inherit', outline: 'none', width: 160 }}
              />
              <button onClick={handleSavePreset} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'inherit' }}>Save</button>
              <button onClick={() => { setSavingPreset(false); setPresetNameDraft(''); }} style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'inherit' }}>Cancel</button>
            </>
          ) : (
            <button onClick={() => setSavingPreset(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 7,
              border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)',
              fontSize: '0.78rem', fontFamily: 'inherit', cursor: 'pointer',
            }}>
              <Plus size={13} /> Save as preset
            </button>
          )
        )}
      </div>

      {selectedOpp && (
        <p style={{ fontSize: '0.78rem', color: 'var(--text-dimmed)', marginTop: -8, marginBottom: 16 }}>vs {selectedOpp.name}</p>
      )}

      {(drillsLoading) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '30vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading match segments…</div>
      )}

      {!drillsLoading && availableDrills.length === 0 && (
        <div style={{ ...card, padding: 48, textAlign: 'center', marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            No 5-minute period segments found for this match. Make sure period drills (Match-0-5, Match-5-10, …) were uploaded.
          </p>
        </div>
      )}

      {!drillsLoading && availableDrills.length > 0 && (
        <>
          {/* ── Slot builder ── */}
          <div style={{ ...card, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Slots</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {slots.map((slot, i) => (
                <SlotCard
                  key={slot.id}
                  slot={slot}
                  index={i}
                  availableDrills={availableDrills}
                  usedInOtherSlots={usedByOtherSlot(slot.id)}
                  autoLabel={autoLabel(slot)}
                  onSetDrills={drills => setSlotDrills(slot.id, drills)}
                  onSetLabel={label => setSlotLabel(slot.id, label)}
                  onRemove={() => removeSlot(slot.id)}
                />
              ))}
              <button onClick={addSlot} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                minWidth: 100, flex: '0 0 100px', borderRadius: 10, border: '1px dashed var(--border-strong)',
                background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit',
              }}>
                <Plus size={18} /> Add slot
              </button>
            </div>
          </div>

          {/* ── Chart ── */}
          <div style={{ ...card, padding: '20px 20px 12px' }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>
              {metric1Label}{unitSuffix}{!isMultiPlayer && metric2 ? ` vs ${metric2Label}${unitSuffix}` : ''}
              {' '}— {scope === 'team' ? 'Squad average'
                : playerIds.length === 0 ? 'Select players'
                : isMultiPlayer ? `${playerIds.length} players`
                : (players.find(p => String(p.id) === playerIds[0])?.name || '')}
            </div>

            {scope === 'player' && playerIds.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, color: 'var(--text-dimmed)', fontSize: '0.85rem' }}>
                Select at least one player to see data
              </div>
            ) : chartData.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, color: 'var(--text-dimmed)', fontSize: '0.85rem' }}>
                {metricsLoading ? 'Loading…' : 'Add drills to a slot to see the comparison'}
              </div>
            ) : (
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={chartData} margin={{ top: 24, right: !isMultiPlayer && metric2 ? 16 : 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 11, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
                      axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                    {!isMultiPlayer && metric2 && (
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
                        axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                    )}
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                      formatter={(v, name) => [fmt0(v) + unitSuffix, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'Inter, sans-serif' }} />
                    {isMultiPlayer ? (
                      rankedSeriesList.map(series => (
                        <Bar key={series.key} yAxisId="left" dataKey={series.label} name={series.label} fill={series.color} radius={[3, 3, 0, 0]} maxBarSize={40}>
                          <LabelList dataKey={series.label} position="top" formatter={fmt0} fill={series.color} fontSize={10} fontFamily="Inter, sans-serif" />
                        </Bar>
                      ))
                    ) : (
                      <>
                        <Bar yAxisId="left" dataKey={metric1} name={metric1Label} fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={60}>
                          <LabelList dataKey={metric1} position="top" formatter={fmt0} fill="var(--text-secondary)" fontSize={11} fontFamily="Inter, sans-serif" />
                        </Bar>
                        {metric2 && (
                          <Line yAxisId="right" type="monotone" dataKey={metric2} name={metric2Label} stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4, fill: '#f59e0b' }} activeDot={{ r: 6 }}>
                            <LabelList dataKey={metric2} position="top" formatter={fmt0} fill="#f59e0b" fontSize={11} fontFamily="Inter, sans-serif" />
                          </Line>
                        )}
                      </>
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
